import { useCallback, useEffect, useRef, useState } from "react";
import { useFormAPI, useFormStore } from "@/widgets/classic-form/form";
import { useTreesQuery } from "@/entities/tree";
import { useUIComponentLoaded } from "@/shared/hooks/useUIComponentLoaded";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";
import { QuickFillPanel } from "./QuickFillPanel";

const MIN_CHARS = 3;

export const QuickFill = () => {
    const { data: trees } = useTreesQuery();
    const formApi = useFormAPI();
    const { givenName, surname } = useFormStore((s) => s.values.name);
    const isCarouselAvailable = useUIComponentLoaded("carousel");
    const track = useTrackSearchFormInteraction();

    const [dismissed, setDismissed] = useState(false);
    const dismissedNameRef = useRef<{ givenName: string; surname: string } | null>(null);

    const meetsThreshold = givenName.length >= MIN_CHARS || surname.length >= MIN_CHARS;

    // Re-trigger after dismissal when name values change
    useEffect(() => {
        if (!dismissed || !dismissedNameRef.current) return;
        const nameChanged =
            dismissedNameRef.current.givenName !== givenName || dismissedNameRef.current.surname !== surname;
        if (nameChanged && meetsThreshold) {
            setDismissed(false);
            dismissedNameRef.current = null;
        }
    }, [dismissed, givenName, surname, meetsThreshold]);

    const handleDismiss = useCallback(() => {
        track({ action: "dismiss_panel", objectName: "quickfill" });
        setDismissed(true);
        dismissedNameRef.current = { givenName, surname };
        formApi.setFieldValue("treePerson", { treeId: "", personId: "" });
        formApi.setFieldValue("showUnviewedRecordsOnly", false);
    }, [givenName, surname, formApi, track]);

    if (!isCarouselAvailable) return null;
    if (!trees?.trees?.length) return null;
    if (!meetsThreshold) return null;
    if (dismissed) return null;

    return <QuickFillPanel onDismiss={handleDismiss} />;
};
